import { sendGTMEvent } from '@next/third-parties/google'
import { toast } from 'sonner'
import { createEnrolmentAction } from '@/app/(common)/cursos/info/_actions/create-enrolment-action'
import { checkExistEnrolment } from '@/app/(common)/cursos/info/_actions/exist-enrolment-action'
import type { EnumProductType } from '@/types/enum-product-type'
import { buildGA4EventModel } from '../@analitycs/push-ecommerce-event'

const TOAST_DURATION = 10_000
const CHECKOUT_URL = '/checkout/payment/resume'

interface EnrolmentData {
  product_id: string
  workload_id: string
  period_id: string
}

interface EnrolAfterAuthenticationParams {
  enrolmentData: EnrolmentData
  externalIdProduct: string | null
  referenceProduct: string
  productType: EnumProductType
}

/**
 * Navegação completa (não SPA) após a inscrição feita logo depois do
 * login/cadastro no modal. A sessão acabou de ser criada nesta mesma página,
 * então o Router Cache do client ainda guarda payloads renderizados sem a
 * sessão; um router.push aqui podia ser sobrescrito pelo refresh disparado
 * pelas server actions (login + updateTag) e o usuário voltava a ver o
 * pedido de inscrição. O hard navigation garante que a sala seja
 * renderizada do zero, já com os cookies novos.
 */
function navigateTo(url: string) {
  window.location.assign(url)
}

async function trackAddToCart(
  productId: string,
  enrolment: { id: string; product: { name: string; amount: number } }
) {
  try {
    const eventModel = await buildGA4EventModel({
      currency: 'BRL',
      value: enrolment.product.amount,
      items: [
        {
          id: productId,
          name: enrolment.product.name,
          price: enrolment.product.amount,
          quantity: 1,
        },
      ],
    })

    sendGTMEvent({ ecommerce: null })
    sendGTMEvent({
      event: 'add_to_cart',
      eventModel,
      ecommerce: eventModel,
      content_ids: enrolment.id,
      ...(eventModel.user_data && { user_data: eventModel.user_data }),
    })
  } catch {
    // Analytics é best-effort e não deve bloquear a inscrição.
  }
}

/**
 * Conclui a inscrição de um usuário que acabou de se autenticar (login ou
 * cadastro) pelo modal de inscrição.
 *
 * Usa a mesma server action do botão de inscrição (createEnrolmentAction),
 * que além de criar a matrícula trata carrinho/pagamento de produtos
 * gratuitos, pré-pagos e trilhas e expira o cache da matrícula. Só
 * redireciona quando a matrícula foi realmente criada (ou já existia).
 *
 * @returns `true` se o usuário foi redirecionado.
 */
export async function enrolAfterAuthentication({
  enrolmentData,
  externalIdProduct,
  referenceProduct,
  productType,
}: EnrolAfterAuthenticationParams): Promise<boolean> {
  const classroomUrl = `/app/sala/curso/${externalIdProduct ?? referenceProduct}`

  try {
    const enrolmentExists = await checkExistEnrolment({
      reference: referenceProduct,
    })

    if (enrolmentExists?.data?.success) {
      navigateTo(classroomUrl)
      return true
    }

    const result = await createEnrolmentAction({
      product_id: enrolmentData.product_id,
      workload_id: enrolmentData.workload_id,
      period_id: enrolmentData.period_id,
      product_external_id: externalIdProduct,
    })

    const enrolment = result?.data?.data

    if (result?.serverError || result?.data?.error || !enrolment) {
      toast.error('Erro ao realizar inscrição', {
        description: 'Tente novamente em alguns instantes',
        duration: TOAST_DURATION,
      })
      return false
    }

    if ('cart' in enrolment && enrolment.cart) {
      await trackAddToCart(enrolmentData.product_id, enrolment)
    }

    toast.success('Inscrição realizada com sucesso!', {
      description: 'Aguarde o redirecionamento',
    })

    const shouldGoToClassroom =
      productType === 'course' || productType === 'webinar'

    navigateTo(shouldGoToClassroom ? classroomUrl : CHECKOUT_URL)
    return true
  } catch {
    toast.error('Erro ao realizar inscrição', {
      description: 'Tente novamente em alguns instantes',
      duration: TOAST_DURATION,
    })
    return false
  }
}
